import React, { useEffect, useState, useRef, useMemo } from "react";
import GeoSearch from "../components/GeoSearch";
import DisplayField from "./DisplayField";
import EditFieldLabel from "./EditFieldLabel";

function formatDateForDisplay(value) {
    if (!value) return "";
    try {
        const d = new Date(value);
        if (isNaN(d)) return String(value);
        return d.toLocaleString();
    } catch {
        return String(value);
    }
}

export default function PointForm({
    initialData = {},
    title = "Point",
    submitLabel = "Save",
    saving = false,
    externalError = null,
    onSubmit,
    onCancel
}) {
    const apiBase = process.env.REACT_APP_SERVER_API_URL || "";
    const base = apiBase.replace(/\/$/, "");
    console.log(initialData);
    // keep inputs as strings where appropriate to allow empty values in inputs
    const [name, setName] = useState(initialData.name ?? "");
    const [civicAddress, setCivicAddress] = useState(initialData.civicAddress ?? "");
    const [lat, setLat] = useState(initialData.lat ?? "");
    const [lng, setLng] = useState(initialData.lng ?? "");
    const [email, setEmail] = useState(initialData.email ?? "");
    const [phone, setPhone] = useState(initialData.phone ?? "");
    const [facebook, setFacebook] = useState(initialData.facebook ?? "");
    const [instagram, setInstagram] = useState(initialData.instagram ?? "");
    const [website, setWebsite] = useState(initialData.website ?? "");

    // New / updated flat fields on MapPoint
    const [colour, setColour] = useState(initialData.colour ?? "#000000");

    // CategoryTypes: support multiple selections
    const computeInitialCategoryTypes = () => {
        const v = initialData.categoryTypes ?? initialData.CategoryTypes ?? initialData.categoryType ?? initialData.CategoryType ?? "";
        if (Array.isArray(v)) return v;
        if (typeof v === "string" && v.trim()) {
            // support comma-separated string from older payloads
            return v.split(",").map(s => s.trim()).filter(Boolean);
        }
        return [];
    };
    const [categoryTypes, setCategoryTypes] = useState(computeInitialCategoryTypes());

    const [hoursOfOperation, setHoursOfOperation] = useState(initialData.hoursOfOperation ?? "");
    const [membershipRequired, setMembershipRequired] = useState(initialData.membershipRequired ?? false);
    const [detailedServices, setDetailedServices] = useState(initialData.detailedServices ?? "");
    const [fundingBusinessModel, setFundingBusinessModel] = useState(initialData.fundingBusinessModel ?? "");

    // Community context (flattened)
    const [traditionalTerritory, setTraditionalTerritory] = useState(initialData.traditionalTerritory ?? "");
    const [indigenousAffiliationAndEquityInvolvement, setIndigenousAffiliationAndEquityInvolvement] = useState(initialData.indigenousAffiliationAndEquityInvolvement ?? "");
    const [slgbtqiaFocused, setSlgbtqiaFocused] = useState(initialData.slgbtqiaFocused ?? "");
    const [accessibilityAccommodations, setAccessibilityAccommodations] = useState(initialData.accessibilityAccommodations ?? "");

    // Operational / data management
    const [dataSource, setDataSource] = useState(initialData.dataSource ?? initialData.DataSource ?? initialData.operationalDataManagement?.dataSource ?? "");
    const [lastUpdatedDisplay, setLastUpdatedDisplay] = useState(formatDateForDisplay(initialData.lastUpdated ?? initialData.LastUpdated));
    const [verified, setVerified] = useState((initialData.verified ?? initialData.Verified ?? initialData.operationalDataManagement?.verified) ?? false);
    const [notes, setNotes] = useState(initialData.notes ?? initialData.Notes ?? initialData.operationalDataManagement?.notes ?? "");

    const [localError, setLocalError] = useState(null);

    // Dropdown options
    const FALLBACK_CATEGORY_OPTIONS = [
        { value: "", label: "" },
    ];
    const FALLBACK_DETAILED_SERVICES_OPTIONS = [
        { value: "", label: "" },
    ];
    const FALLBACK_FUNDING_OPTIONS = [
        { value: "", label: "" },
    ];

    const [categoryOptions, setCategoryOptions] = useState(FALLBACK_CATEGORY_OPTIONS);
    const [detailedServicesOptions, setDetailedServicesOptions] = useState(FALLBACK_DETAILED_SERVICES_OPTIONS);
    const [fundingOptions, setFundingOptions] = useState(FALLBACK_FUNDING_OPTIONS);

    // load enum options from backend when component mounts
    useEffect(() => {
        let cancelled = false;

        async function loadEnum(enumName, setOptions, fallback) {
            try {
                const res = await fetch(`${base}/api/enums/${enumName}`);
                if (!res.ok) {
                    return;
                }
                console.log("got data");
                const data = await res.json();
                console.log(data);
                if (cancelled) return;
                // map { value, display, color? } -> { value, label, color }
                const opts = [{ value: "", label: "" }, ...data.map(i => ({ value: i.value, label: i.display, color: i.color }))];
                setOptions(opts);
            } catch {
                // ignore and keep fallback
            }
        }

        loadEnum("CategoryTypeEnum", setCategoryOptions, FALLBACK_CATEGORY_OPTIONS);
        loadEnum("ServicesEnum", setDetailedServicesOptions, FALLBACK_DETAILED_SERVICES_OPTIONS);
        loadEnum("FundingTypeEnum", setFundingOptions, FALLBACK_FUNDING_OPTIONS);

        return () => { cancelled = true; };
    }, [base, FALLBACK_CATEGORY_OPTIONS, FALLBACK_DETAILED_SERVICES_OPTIONS, FALLBACK_FUNDING_OPTIONS]);

    // compute allowed colour options based on currently selected categoryTypes
    const allowedColourOptions = useMemo(() => {
        if (!Array.isArray(categoryTypes) || categoryTypes.length === 0) return [];
        if (!Array.isArray(categoryOptions) || categoryOptions.length === 0) return [];

        // collect categoryOptions that match selected categoryTypes
        const matched = categoryTypes
            .map(ct => categoryOptions.find(o => o.value === ct))
            .filter(Boolean);

        // map to unique colours (unique by colour hex)
        const seen = new Set();
        const opts = [];
        for (const m of matched) {
            const col = (m.color || "#000000").trim();
            if (!col) continue;
            if (!seen.has(col)) {
                seen.add(col);
                opts.push({ color: col, label: m.label || m.value, sourceValue: m.value });
            }
        }
        return opts;
    }, [categoryTypes, categoryOptions]);

    // when allowed colours change, ensure current colour is valid; fallback to first allowed or keep default
    useEffect(() => {
        if (!Array.isArray(allowedColourOptions)) return;
        if (allowedColourOptions.length === 0) {
            // no categories selected -> leave colour as-is (or you could reset to default)
            return;
        }
        const current = (colour || "").trim();
        const found = allowedColourOptions.find(a => a.color.toLowerCase() === current.toLowerCase());
        if (!found) {
            setColour(allowedColourOptions[0].color);
        }
    }, [allowedColourOptions]); // eslint-disable-line react-hooks/exhaustive-deps

    // keep local state synced when initialData changes (edit case)
    useEffect(() => {
        setName(initialData.name ?? initialData.Name ?? "");
        setCivicAddress(initialData.civicAddress ?? initialData.CivicAddress ?? "");
        setLat(initialData.lat ?? initialData.Lat ?? "");
        setLng(initialData.lng ?? initialData.Lng ?? "");
        setEmail(initialData.email ?? initialData.Email ?? "");
        setPhone(initialData.phone ?? initialData.Phone ?? "");
        setFacebook(initialData.facebook ?? "");
        setInstagram(initialData.instagram ?? "");
        setWebsite(initialData.website ?? "");

        setColour(initialData.colour ?? initialData.Colour ?? "#000000");

        // sync multi-select initial value
        const newCats = computeInitialCategoryTypes();
        setCategoryTypes(newCats);

        setHoursOfOperation(initialData.hoursOfOperation ?? initialData.HoursOfOperation ?? "");
        setMembershipRequired((initialData.membershipRequired ?? initialData.MembershipRequired) ?? false);
        setDetailedServices(initialData.detailedServices ?? initialData.DetailedServices ?? "");
        setFundingBusinessModel(initialData.fundingBusinessModel ?? "");

        setTraditionalTerritory(initialData.traditionalTerritory ?? initialData.TraditionalTerritory ?? "");
        setIndigenousAffiliationAndEquityInvolvement(initialData.indigenousAffiliationAndEquityInvolvement ?? initialData.IndigenousAffiliationAndEquityInvolvement ?? "");
        setSlgbtqiaFocused(initialData.slgbtqiaFocused ?? initialData.SLGBTQIAFocused ?? "");
        setAccessibilityAccommodations(initialData.accessibilityAccommodations ?? initialData.AccessibilityAccommodations ?? "");

        setDataSource(initialData.dataSource ?? initialData.DataSource ?? "");
        setLastUpdatedDisplay(formatDateForDisplay(initialData.lastUpdated));
        setVerified(initialData.verified ?? false);
        setNotes(initialData.notes ?? "");

        setLocalError(null);
    }, [initialData]);

    function buildPayload() {
        // send keys matching server MapPoint property names (PascalCase)
        const payload = {
            Lat: parseFloat(lat),
            Lng: parseFloat(lng),
            Name: name || "Point",
            CivicAddress: civicAddress,
            Email: email || null,
            Phone: phone || null,
            Facebook: facebook || null,
            Instagram: instagram || null,
            Website: website || null,

            Colour: colour || "#000000",
            // send CategoryTypes as an array of values (backend expects CategoryTypes)
            CategoryTypes: (Array.isArray(categoryTypes) && categoryTypes.length > 0) ? categoryTypes : null,
            HoursOfOperation: hoursOfOperation || null,
            MembershipRequired: membershipRequired,
            DetailedServices: detailedServices || null,
            FundingBusinessModel: fundingBusinessModel || null,

            TraditionalTerritory: traditionalTerritory || null,
            IndigenousAffiliationAndEquityInvolvement: indigenousAffiliationAndEquityInvolvement || null,
            SLGBTQIAFocused: slgbtqiaFocused || null,
            AccessibilityAccommodations: accessibilityAccommodations || null,

            DataSource: dataSource || null,
            Verified: !!verified,
            Notes: notes || null
        };

        return payload;
    }

    async function submit(e) {
        e.preventDefault();
        setLocalError(null);

        const parsedLat = parseFloat(lat);
        const parsedLng = parseFloat(lng);
        if (Number.isNaN(parsedLat) || Number.isNaN(parsedLng)) {
            setLocalError("Latitude and Longitude must be valid numbers.");
            return;
        }
        if (!name.trim()) {
            setLocalError("Name is required.");
            return;
        }

        const payload = buildPayload();
        if (typeof onSubmit === "function") {
            await onSubmit(payload);
        }
    }

    // custom dropdown state for category select (simple, shows swatch in list)
    const [catOpen, setCatOpen] = useState(false);
    const catRef = useRef(null);

    // colour dropdown state (custom dropdown so we can show swatches in the list)
    const [colourOpen, setColourOpen] = useState(false);
    const colourRef = useRef(null);

    // close custom dropdowns on outside click
    useEffect(() => {
        function onDocClick(e) {
            if (catRef.current && !catRef.current.contains(e.target)) setCatOpen(false);
            if (colourRef.current && !colourRef.current.contains(e.target)) setColourOpen(false);
        }
        if (catOpen || colourOpen) document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [catOpen, colourOpen]);

    // Helper to get display label(s) for current categoryTypes
    const currentCategoryLabel = () => {
        if (!Array.isArray(categoryTypes) || categoryTypes.length === 0) return "";
        const labels = categoryTypes.map(ct => {
            const opt = categoryOptions.find(o => o.value === ct);
            return opt ? (opt.label || opt.value) : ct;
        });
        return labels.join(", ");
    };

    const currentCategoryColor = () => {
        if (!Array.isArray(categoryTypes) || categoryTypes.length === 0) return "#000000";
        const opt = categoryOptions.find(o => o.value === categoryTypes[0]);
        return opt ? (opt.color || "#000000") : "#000000";
    };

    // toggle selection helper
    const toggleCategory = (val) => {
        if (!val) return;
        setCategoryTypes(prev => {
            const exists = prev.includes(val);
            if (exists) return prev.filter(x => x !== val);
            return [...prev, val];
        });
    };

    return (
        <div className="card">
            <div className="card-body">
                <form onSubmit={submit}>

                    <div
                        className="sticky-top sticky-action bg-white"
                        style={{ zIndex: 1100, paddingTop: 8, paddingBottom: 8, borderBottom: "1px solid #e9ecef" }}
                    >
                        <h1 className="magnoHeader"><strong>{title}</strong></h1>
                        <div className="d-flex justify-content-end">
                            <div>
                                <button className="btn btn-primary" type="submit" disabled={saving}>
                                    {saving ? (submitLabel + "�") : submitLabel}
                                </button>
                                <button type="button" className="btn btn-secondary ms-2" onClick={onCancel}>Cancel</button>
                            </div>
                        </div>
                    </div>

                    <div className="mb-3">
                        <DisplayField label="Name" value={name} onChange={e => setName(e.target.value)} />
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3" style={{ position: "relative" }}>
                            <EditFieldLabel label="Address" htmlFor="civicAddressInput" />
                            <GeoSearch
                                id="civicAddressInput"
                                value={civicAddress}
                                onChange={v => setCivicAddress(v)}
                                onSelect={s => {
                                    setCivicAddress(s.formatted || "");
                                    if (typeof s.lat === "number") setLat(s.lat);
                                    if (typeof s.lon === "number") setLng(s.lon);
                                }}
                                country="ca"
                                placeholder="Start typing an address..."
                                className="form-control"
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <DisplayField label="Email" value={email} onChange={e => setEmail(e.target.value)} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <DisplayField label="Latitude" value={lat} onChange={e => setLat(e.target.value)} />
                        </div>
                        <div className="col-md-6 mb-3">
                            <DisplayField label="Longitude" value={lng} onChange={e => setLng(e.target.value)} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <DisplayField label="Phone" value={phone} onChange={e => setPhone(e.target.value)} />
                        </div>
                        <div className="col-md-6 mb-3">
                            <DisplayField label="Website" value={website} onChange={e => setWebsite(e.target.value)} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <DisplayField label="Facebook" value={facebook} onChange={e => setFacebook(e.target.value)} />
                        </div>
                        <div className="col-md-6 mb-3">
                            <DisplayField label="Instagram" value={instagram} onChange={e => setInstagram(e.target.value)} />
                        </div>
                    </div>

                    <hr className="my-4" />
                    <h5 className="mb-3 magnoHeader">Asset Details</h5>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <EditFieldLabel label="Category Type" htmlFor="categoryTypeInput" />
                            {/* Custom multi-select dropdown to allow swatches and multiple selection */}
                            <div ref={catRef} style={{ position: "relative" }}>
                                <button
                                    type="button"
                                    className="form-select text-start d-flex align-items-center"
                                    onClick={() => setCatOpen(o => !o)}
                                    aria-haspopup="listbox"
                                    aria-expanded={catOpen}
                                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                                >
                                    <span style={{
                                        width: 16,
                                        height: 16,
                                        background: currentCategoryColor(),
                                        borderRadius: 4,
                                        display: "inline-block",
                                        border: "1px solid rgba(0,0,0,0.12)"
                                    }} />
                                    <span style={{ flex: 1 }}>{currentCategoryLabel() || "Select category(ies)"}</span>
                                </button>

                                {catOpen && (
                                    <div className="card" role="listbox" style={{ position: "absolute", zIndex: 1600, top: "calc(100% + 6px)", left: 0, right: 0, maxHeight: 260, overflow: "auto", padding: 8 }}>
                                        {categoryOptions.map(opt => {
                                            // keep empty placeholder visible at top but skip rendering an empty swatch row
                                            const isEmpty = !opt.value;
                                            const checked = categoryTypes.includes(opt.value);
                                            return (
                                                <div key={opt.value || ""} role="option" aria-selected={checked}
                                                    onClick={() => { if (!isEmpty) toggleCategory(opt.value); }}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 8,
                                                        padding: "6px 8px",
                                                        cursor: isEmpty ? "default" : "pointer",
                                                        background: checked ? "rgba(0,0,0,0.04)" : "transparent",
                                                        borderRadius: 4,
                                                        opacity: isEmpty ? 0.7 : 1
                                                    }}
                                                >
                                                    {/* small swatch for each option */}
                                                    <span style={{
                                                        width: 14,
                                                        height: 14,
                                                        background: isEmpty ? "transparent" : (opt.color || "#000"),
                                                        borderRadius: 3,
                                                        display: "inline-block",
                                                        border: isEmpty ? "none" : "1px solid rgba(0,0,0,0.12)"
                                                    }} />
                                                    <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                        {opt.label || opt.value}
                                                    </span>
                                                    {!isEmpty && (
                                                        <input type="checkbox" readOnly tabIndex={-1} checked={checked} style={{ marginLeft: 8 }} />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="col-md-6 mb-3">
                            <EditFieldLabel label="Detailed Services" htmlFor="detailedServicesInput" />
                            <select
                                id="detailedServicesInput"
                                className="form-select"
                                value={detailedServices}
                                onChange={e => setDetailedServices(e.target.value)}>
                                {detailedServicesOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <EditFieldLabel label="Funding / Business Model" htmlFor="fundingBusinessModelInput" />
                            <select
                                id="fundingBusinessModelInput"
                                className="form-select"
                                value={fundingBusinessModel}
                                onChange={e => setFundingBusinessModel(e.target.value)}>
                                {fundingOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                        </div>
                        <div className="col-md-6 mb-3">
                            <EditFieldLabel label="Colour" htmlFor="colourInput" />
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                {/* Custom colour dropdown so each option can show a swatch like Category Type */}
                                <div ref={colourRef} style={{ position: "relative", flex: 1 }}>
                                    <button
                                        type="button"
                                        id="colourInput"
                                        className="form-select text-start d-flex align-items-center"
                                        onClick={() => setColourOpen(o => !o)}
                                        aria-haspopup="listbox"
                                        aria-expanded={colourOpen}
                                        style={{ display: "flex", gap: 8, alignItems: "center" }}
                                        disabled={allowedColourOptions.length === 0}
                                    >
                                        <span style={{
                                            width: 16,
                                            height: 16,
                                            background: colour || "transparent",
                                            borderRadius: 4,
                                            display: "inline-block",
                                            border: "1px solid rgba(0,0,0,0.12)"
                                        }} />
                                        <span style={{ flex: 1 }}>
                                            {allowedColourOptions.length === 0 ? "Select category(ies) first" :
                                                (allowedColourOptions.find(a => a.color.toLowerCase() === (colour || "").toLowerCase())?.label || colour)}
                                        </span>
                                    </button>

                                    {colourOpen && allowedColourOptions.length > 0 && (
                                        <div className="card" role="listbox" style={{ position: "absolute", zIndex: 1600, top: "calc(100% + 6px)", left: 0, right: 0, maxHeight: 260, overflow: "auto", padding: 8 }}>
                                            {allowedColourOptions.map(opt => {
                                                const isSelected = (opt.color || "").toLowerCase() === (colour || "").toLowerCase();
                                                return (
                                                    <div key={opt.color} role="option" aria-selected={isSelected}
                                                        onClick={() => { setColour(opt.color); setColourOpen(false); }}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 8,
                                                            padding: "6px 8px",
                                                            cursor: "pointer",
                                                            background: isSelected ? "rgba(0,0,0,0.04)" : "transparent",
                                                            borderRadius: 4
                                                        }}
                                                    >
                                                        <span style={{
                                                            width: 14,
                                                            height: 14,
                                                            background: opt.color,
                                                            borderRadius: 3,
                                                            display: "inline-block",
                                                            border: "1px solid rgba(0,0,0,0.12)"
                                                        }} />
                                                        <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                            {opt.label}
                                                        </span>
                                                        <input type="radio" readOnly tabIndex={-1} checked={isSelected} style={{ marginLeft: 8 }} />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* visual preview swatch */}
                                <span style={{
                                    width: 36,
                                    height: 36,
                                    background: colour || "transparent",
                                    border: "1px solid rgba(0,0,0,0.12)",
                                    borderRadius: 4,
                                    display: "inline-block"
                                }} />
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <div className="form-check">
                                <input
                                    id="membershipRequired"
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={membershipRequired}
                                    onChange={e => setMembershipRequired(e.target.checked)} />
                                <EditFieldLabel label="Membership Required" htmlFor="membershipRequired" className="form-check-label sudHeader" />
                            </div>
                        </div>
                        <div className="col-md-6 mb-3">
                            <DisplayField
                                label="Hours of Operation"
                                value={hoursOfOperation}
                                onChange={e => setHoursOfOperation(e.target.value)}
                                multiline
                                rows={4}
                            />
                        </div>
                    </div>

                    <hr className="my-4" />
                    <h5 className="mb-3 magnoHeader">Community Context</h5>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <DisplayField label="Traditional Territory" value={traditionalTerritory} onChange={e => setTraditionalTerritory(e.target.value)} />
                        </div>
                        <div className="col-md-6 mb-3">
                            <DisplayField label="Indigenous Affiliation & Equity Involvement" value={indigenousAffiliationAndEquityInvolvement} onChange={e => setIndigenousAffiliationAndEquityInvolvement(e.target.value)} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <DisplayField label="2SLGBTQIA+ Focused" value={slgbtqiaFocused} onChange={e => setSlgbtqiaFocused(e.target.value)} />
                        </div>
                        <div className="col-md-6 mb-3">
                            <DisplayField label="Accessibility Accommodations" value={accessibilityAccommodations} onChange={e => setAccessibilityAccommodations(e.target.value)} />
                        </div>
                    </div>

                    <hr className="my-4" />

                    <h5 className="mb-3 magnoHeader">Data Management</h5>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <DisplayField label="Data Source" value={dataSource} onChange={e => setDataSource(e.target.value)} />
                        </div>
                        <div className="col-md-6 mb-3">
                            <DisplayField label="Last Updated" value={lastUpdatedDisplay} readOnly />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <div className="form-check">
                                <input
                                    id="verifiedCheck"
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={verified}
                                    onChange={e => setVerified(e.target.checked)} />
                                <EditFieldLabel label="Verified" htmlFor="verifiedCheck" className="form-check-label sudHeader" />
                            </div>
                        </div>
                        <div className="col-md-6 mb-3">
                            <DisplayField label="Notes" value={notes} onChange={e => setNotes(e.target.value)} />
                        </div>
                    </div>

                    {(localError || externalError) && <div className="alert alert-danger">{localError || externalError}</div>}
                </form>
            </div>
        </div>
    );
}